import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { Alert } from "@krea/shared/design-system/alerts/Alert";
import { Button } from "@krea/shared/design-system/Button";
import { Card, CardContent } from "@krea/shared/design-system/Card";
import { ControlledWrapper } from "@krea/shared/design-system/input/controlled-wrapper";
import { Input } from "@krea/shared/design-system/input/input";
import { Separator } from "@krea/shared/design-system/Separator";
import { toast } from "@krea/shared/design-system/utils/toast";

import { useCreateTestLenderApplicationMutation } from "@/features/lender-applications/hooks/data/use-create-test-lender-application.mutation";

import { useGetCurrentLenderCompanyQuery } from "../hooks/use-get-lender-company.query";

const schema = yup.object().shape({
  organisationNumber: yup
    .string()
    .nullable()
    .test(
      "organisation-number-min-length",
      "Organisation number must be at least 9 characters long",
      (value) => !value || value.length >= 9,
    ),
  requestedAmount: yup
    .number()
    .nullable()
    .min(1_000, "The requested loan amount must be at least 1 000"),
});

export const TestYourAPISection = () => {
  const form = useForm<yup.InferType<typeof schema>>({
    resolver: yupResolver(schema),
    defaultValues: {
      organisationNumber: null,
      requestedAmount: null,
    },
  });
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { mutate: createTestLenderApplication, isPending } =
    useCreateTestLenderApplicationMutation();

  const handleSubmit = useCallback(() => {
    if (!lenderCompany) return;
    form.handleSubmit((data) => {
      createTestLenderApplication(
        {
          lenderCompanyId: lenderCompany.id,
          data: {
            organisationNumber: data.organisationNumber ?? undefined,
            requestedAmount: data.requestedAmount ?? undefined,
          },
        },
        {
          onSuccess: () => {
            toast.success("Test application created successfully");
          },
          onError: () => {
            toast.error("Failed to create test application");
          },
        },
      );
    })();
  }, [createTestLenderApplication, form, lenderCompany]);

  return (
    <Card>
      <CardContent className="space-y-6 pb-8">
        <div className="">
          <h1 className="text-section">Test your API</h1>
          <p className="text-base" />
        </div>

        <Separator />
        <div>
          <Alert variant="info">
            <Alert.Title>
              Test your API by creating a new loan application for testing.
            </Alert.Title>
            <Alert.Description>
              This form will create a new loan application for testing.
              <ul className="list-inside list-disc">
                <li>
                  You will receive a notification about the new loan application
                  on your webhook endpoint.
                </li>
                <li>Use the api to read the lender application data</li>
                <li>
                  On the detail page of the application after you have created
                  it, you will be able to click "view as customer" next to the
                  organisation number. This will open the customer app in a new
                  tab.
                </li>
              </ul>
            </Alert.Description>
          </Alert>

          <div className="space-y-5">
            <ControlledWrapper
              control={form.control}
              name="organisationNumber"
              label="Organisation Number"
              render={({ field: { value, ...field }, fieldState }) => (
                <>
                  <Input
                    type="text"
                    placeholder="1234567890"
                    value={value ?? ""}
                    {...field}
                    disabled={form.formState.isSubmitting}
                  />
                  {!fieldState.invalid && (
                    <p className="text-[0.8rem] text-gray-600">
                      The organisation number of the company you want to test.
                      (leave blank to use the current lender company's
                      organisation number)
                    </p>
                  )}
                </>
              )}
            />

            <ControlledWrapper
              control={form.control}
              name="requestedAmount"
              label="Requested Amount"
              render={({ field: { value, ...field }, fieldState }) => (
                <>
                  <Input
                    placeholder="10 000"
                    value={value ?? ""}
                    {...field}
                    disabled={form.formState.isSubmitting}
                  />
                  {!fieldState.invalid && (
                    <p className="text-[0.8rem] text-gray-600">
                      The amount of money you want to request.
                    </p>
                  )}
                </>
              )}
            />
          </div>
        </div>

        <Separator />
        <div className="flex justify-end">
          <Button
            type="button"
            size="lg"
            onClick={handleSubmit}
            loading={isPending}
            disabled={isPending || !form.formState.isValid}
          >
            Create Test Application
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
